import { useRef } from "react";
import toast from "react-hot-toast";
import { useMedia } from "react-use";
import { hasArbitraryRange } from "@/entities/player";
import {
  hasOnlyFilterRange,
  isPlayerIdle,
  updateCurrentTime,
  updateVideoStream,
} from "@/entities/player";
import { useAppDispatch, useAppStore } from "@/app/appStore";
import {
  calculateSeekFromDVRWindowStart,
  calculateArbitraryDateRange,
  isArbitraryRangeWithinLive,
  isBehindDVRWindow,
} from "@/shared/helpers";
import { useMediaPlayer } from "@/shared/ui";
import { isOutsideAvailabilityWindowRange } from "@/shared/helpers/is-outside-availability-window-range";

export function useTimelineSeekHandlers() {
  const mediaPlayer = useMediaPlayer();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const isSeeking = useRef(false);
  const isMobile = useMedia("(max-width: 768px)");

  const seekTo = (time: Date) => {
    mediaPlayer.seek(calculateSeekFromDVRWindowStart(time, mediaPlayer));
  };

  const playLiveOrArbitraryRange = (
    timeEpoch: number,
    padding: number,
    liveDelayThreshold: number
  ) => {
    const [arbitraryFromTime, arbitraryToTime] = calculateArbitraryDateRange(
      timeEpoch,
      padding
    );

    if (
      isArbitraryRangeWithinLive(
        arbitraryFromTime,
        arbitraryToTime,
        liveDelayThreshold
      )
    ) {
      dispatch(
        updateVideoStream({
          fromTime: "",
          toTime: "",
          arbitraryFromTime: null,
          arbitraryToTime: null,
          isLive: true,
        })
      );
      return;
    }

    dispatch(
      updateVideoStream({
        arbitraryFromTime,
        arbitraryToTime,
        isLive: false,
        fromTime: "",
        toTime: "",
      })
    );
    dispatch(updateCurrentTime(timeEpoch));
  };

  const handleSeekArbitraryTime = (event: any) => {
    const state = store.getState();
    const {
      preferences: { padding, liveDelayThreshold },
      videoStream: {
        isLive,
        cameraId,
        availabilityStartTime,
        computedAvailabilityEndTime,
      },
    } = state.player;
    const timeEpoch = new Date(event.time).getTime();

    if (!cameraId) {
      toast.error("Please, select a camera");
      return;
    }

    if (isPlayerIdle(state)) {
      return playLiveOrArbitraryRange(timeEpoch, padding, liveDelayThreshold);
    }

    const dvrWindow = mediaPlayer.getDashMetrics().getCurrentDVRInfo();
    const outsideAvailabilityWindowRange = isOutsideAvailabilityWindowRange(
      timeEpoch,
      availabilityStartTime,
      computedAvailabilityEndTime
    );
    const behindDVRWindow = isBehindDVRWindow(timeEpoch, dvrWindow);

    if (hasOnlyFilterRange(state) && outsideAvailabilityWindowRange) {
      toast.error("Please, reset timeline to seek to arbitrary date time");
      return;
    }

    if (isLive && behindDVRWindow) {
      return playLiveOrArbitraryRange(timeEpoch, padding, liveDelayThreshold);
    }

    if (hasArbitraryRange(state) && outsideAvailabilityWindowRange) {
      return playLiveOrArbitraryRange(timeEpoch, padding, liveDelayThreshold);
    }

    if (!mediaPlayer.isReady()) return;

    seekTo(event.time);
  };

  const onTimeChange = (event: any) => {
    isSeeking.current = true;
    seekTo(event.time);
  };
  const onTimeChanged = (event: any) => {
    handleSeekArbitraryTime(event);
    isSeeking.current = false;
  };

  const onDoubleClick = (event: any) => {
    if (isMobile) return;

    handleSeekArbitraryTime(event);
  };

  const onRangeChange = (event: any) => {
    if (!event.byUser || !isMobile) return;

    const mid = (event.start.getTime() + event.end.getTime()) / 2;

    dispatch(updateCurrentTime(mid));

    if (mediaPlayer.isReady()) {
      seekTo(new Date(mid));
    }
  };

  const onRangeChanged = (event: any) => {
    if (!event.byUser || !isMobile) return;

    const mid = (event.start.getTime() + event.end.getTime()) / 2;
    handleSeekArbitraryTime({ time: new Date(mid) });
  };

  return {
    onTimeChange,
    onTimeChanged,
    onDoubleClick,
    onRangeChange,
    onRangeChanged,
    isSeeking,
  };
}
